import React from 'react';
import { Heart, Palette, Award, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header2';
import Footer from './components/Footer2';

const About = () => {
    const navigate = useNavigate();

    const values = [
        { icon: Palette, title: 'Bold Expression', description: 'We believe fashion should be fearless. Every piece is designed to make you stand out.' },
        { icon: Heart, title: 'Indian Heritage', description: 'Rooted in centuries of textile tradition, celebrating the vibrant spirit of Indian craftsmanship.' },
        { icon: Award, title: 'Master Artisans', description: 'Handcrafted by skilled artisans who bring generations of expertise to every stitch.' },
        { icon: Users, title: 'Community First', description: 'Empowering local artisan communities and creating sustainable livelihoods across India.' },
    ];

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main>
                {/* Hero */}
                <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="About RANG"
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ opacity: 0.4 }}
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.5), #0A0A0A)' }} />
                    <div className="relative z-10 text-center px-4 pt-20">
                        <p className="text-xs font-medium tracking-[0.3em] uppercase mb-4 text-[#FF6B35] font-['Montserrat']">Our Story</p>
                        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Montserrat'] uppercase">The Spirit of RANG</h1>
                        <p className="text-sm md:text-base text-[#A3A3A3] max-w-lg mx-auto font-['Montserrat']">
                            Where bold colours meet timeless tradition. Born from the vibrant streets of India.
                        </p>
                    </div>
                </section>

                {/* Brand Story */}
                <section className="py-16 md:py-24 bg-[#0A0A0A]">
                    <div className="max-w-4xl mx-auto px-4 md:px-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="w-16 h-1 bg-[#FF6B35] mb-6" />
                                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 font-['Montserrat'] uppercase">Born from Colour</h2>
                                <p className="text-sm text-[#A3A3A3] leading-relaxed mb-4 font-['Montserrat']">
                                    RANG was born from a simple belief: Indian fashion deserves to be bold, vibrant, and unapologetically alive.
                                    In a world of muted tones, we chose colour. In a sea of conformity, we chose expression.
                                </p>
                                <p className="text-sm text-[#A3A3A3] leading-relaxed mb-4 font-['Montserrat']">
                                    Our journey began in the bustling lanes of Jaipur, where master artisans have been weaving stories
                                    into fabric for generations. We partnered with these incredible craftspeople to create something
                                    extraordinary: fashion that honours tradition while embracing the energy of modern India.
                                </p>
                                <p className="text-sm text-[#A3A3A3] leading-relaxed font-['Montserrat']">
                                    Every RANG piece is a celebration. From the rich Banarasi silks to the playful fusion designs,
                                    each creation carries the spirit of India's textile legacy — reimagined for the fearless,
                                    fashion-forward woman of today.
                                </p>
                            </div>
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600"
                                    alt="RANG craftsmanship"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Values */}
                <section className="py-16 md:py-24 bg-[#1A1A1A]">
                    <div className="max-w-5xl mx-auto px-4 md:px-6">
                        <div className="text-center mb-12">
                            <p className="text-xs font-medium tracking-[0.3em] uppercase mb-3 text-[#FF6B35] font-['Montserrat']">What We Stand For</p>
                            <h2 className="text-2xl md:text-3xl font-bold text-white font-['Montserrat'] uppercase">Our Values</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {values.map(({ icon: Icon, title, description }) => (
                                <div key={title} className="text-center p-6 bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#FF6B35] transition-colors">
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#FF6B35]/10">
                                        <Icon className="w-6 h-6 text-[#FF6B35]" />
                                    </div>
                                    <h3 className="text-sm font-bold text-white mb-2 font-['Montserrat'] uppercase">{title}</h3>
                                    <p className="text-xs text-[#A3A3A3] leading-relaxed font-['Montserrat']">{description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section className="py-16 md:py-20 bg-[#0A0A0A] border-t border-b border-[#2A2A2A]">
                    <div className="max-w-4xl mx-auto px-4 md:px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            {[
                                { num: '500+', label: 'Artisans' },
                                { num: '10K+', label: 'Happy Customers' },
                                { num: '50+', label: 'Collections' },
                                { num: '15+', label: 'Cities' },
                            ].map(({ num, label }) => (
                                <div key={label}>
                                    <p className="text-2xl md:text-3xl font-bold text-[#FF6B35] mb-1 font-['Montserrat']">{num}</p>
                                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider font-['Montserrat']">{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 md:py-24 bg-[#0A0A0A] text-center">
                    <div className="max-w-xl mx-auto px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat'] uppercase">Ready to Explore?</h2>
                        <p className="text-sm text-[#A3A3A3] mb-8 font-['Montserrat']">
                            Discover our latest collection and find your next statement piece.
                        </p>
                        <button
                            onClick={() => navigate('/template2/shop')}
                            className="inline-flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-[0.15em] uppercase bg-[#FF6B35] text-white hover:bg-[#E85D26] transition-colors font-['Montserrat']"
                        >
                            Shop Now <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default About;
